import { colors } from '@kmns-internal/furikake'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { Theme } from '@mui/material/styles'
import * as Sentry from '@sentry/browser'
import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import { TableActionMenu } from '../TableActionMenu'
import {
  HEADER_CELL_HEIGHT,
  columnWidthSx,
  isColumnInputType,
  renderColumnTypeIcon,
} from '../model'
import { LogicNumberChip } from './LogicNumberChip'
import { TableColumnHeaderCell } from './TableColumnDnd'
import { TableColumnHeaderName } from './TableColumnHeaderName'
import type { TableEditHandlers } from './TablePreview'

type BaseProps = {
  /** 列の質問ノード。 */
  column: TemplateNodeSchema
  /** 並び替えの単位となる列の index。 */
  columnIndex: number
  /** 多段ヘッダーで、自分の段から最下段まで伸ばす段数。 */
  rowSpan: number
  selected: boolean
  /** この列が条件分岐を持つときの通し番号。同じ番号がこの列の帯にも出る。 */
  logicNumber: number | undefined
}

type InteractiveProps = BaseProps & {
  interactive: true
  handlers: TableEditHandlers
}

type ReadonlyProps = BaseProps & {
  interactive?: false
}

type Props = InteractiveProps | ReadonlyProps

const headerCellSx = (theme: Theme, selected: boolean) => ({
  position: 'sticky' as const,
  top: 0,
  zIndex: 2,
  ...columnWidthSx(theme),
  height: HEADER_CELL_HEIGHT(theme),
  p: 0,
  borderRight: 1,
  borderBottom: 1,
  borderColor: 'divider',
  boxSizing: 'border-box' as const,
  verticalAlign: 'middle' as const,
  bgcolor: selected ? colors.primary.selected : colors.white,
})

export const TablePreviewColumnHeader = (props: Props) => {
  const { column, columnIndex, rowSpan, selected, logicNumber } = props

  const responseType = column.question?.responseType
  // 列を省略すると本体と列数がずれるので、回答種別が不正でもセルは必ず描画する。
  const validType =
    responseType != null && isColumnInputType(responseType)
      ? responseType
      : null
  if (validType === null) {
    Sentry.captureException(
      new Error('列の回答種別が未設定、または未対応の種別です'),
      { extra: { column } },
    )
  }

  const menuItems = props.interactive
    ? props.handlers.buildColumnMenuItems(column)
    : []

  const content = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ width: '100%', minWidth: 0, px: 1.5, py: 0.75 }}
    >
      <Box
        sx={theme => ({
          flex: '0 0 auto',
          display: 'inline-flex',
          width: theme.spacing(3),
          height: theme.spacing(3),
        })}
      >
        {validType !== null && renderColumnTypeIcon(validType)}
      </Box>
      {/* 選択はヘッダーセル全体の onClick が担うため、名前は表示専用にする。 */}
      <TableColumnHeaderName
        name={column.question?.name?.trim() || '質問名未設定'}
      />
      {logicNumber && <LogicNumberChip logicNumber={logicNumber} />}
      {props.interactive && menuItems.length > 0 && (
        <TableActionMenu items={menuItems} ariaLabel="列の操作" />
      )}
    </Stack>
  )

  if (props.interactive) {
    return (
      <TableColumnHeaderCell
        columnIndex={columnIndex}
        rowSpan={rowSpan}
        draggable={selected}
        onClick={() => props.handlers.onSelectColumn(column.id)}
        onReorder={props.handlers.onReorderColumn}
        sx={theme => headerCellSx(theme, selected)}
      >
        {content}
      </TableColumnHeaderCell>
    )
  }

  return (
    <Box
      component="th"
      rowSpan={rowSpan}
      sx={theme => headerCellSx(theme, selected)}
    >
      {content}
    </Box>
  )
}
